import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { expensesApi, receiptsApi, propertiesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';
import { Plus, Trash2, Camera, Upload, Loader2, Edit2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const CATEGORIES = [
  { value: 'équipement', label: 'Équipement' },
  { value: 'entretien', label: 'Entretien' },
  { value: 'services', label: 'Services' },
  { value: 'nettoyage', label: 'Nettoyage' },
  { value: 'énergie', label: 'Énergie' },
  { value: 'internet', label: 'Internet' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'autre', label: 'Autre' },
];

const CATEGORY_COLORS = {
  'équipement': 'bg-blue-500/20 text-blue-400',
  'entretien': 'bg-orange-500/20 text-orange-400',
  'services': 'bg-purple-500/20 text-purple-400',
  'nettoyage': 'bg-green-500/20 text-green-400',
  'énergie': 'bg-yellow-500/20 text-yellow-400',
  'internet': 'bg-cyan-500/20 text-cyan-400',
  'taxes': 'bg-red-500/20 text-red-400',
  'autre': 'bg-gray-500/20 text-gray-400',
};

export default function ExpensesPage() {
  const { selectedProperty, properties } = useOutletContext();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    category: 'autre',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    merchant: '',
    receipt_id: '',
  });

  useEffect(() => {
    loadExpenses();
  }, [selectedProperty]);

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const params = selectedProperty !== 'all' ? { property_id: selectedProperty } : {};
      const response = await expensesApi.getAll(params);
      setExpenses(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    console.log('onDrop called:', { acceptedFiles, rejectedFiles });
    
    // Handle rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors) {
        const errorMessages = rejection.errors.map(e => e.message).join(', ');
        toast.error(`Fichier refusé: ${errorMessages}`);
      }
      return;
    }

    const file = acceptedFiles[0];
    if (!file) {
      toast.error('Aucun fichier sélectionné');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);
    toast.info(`Fichier sélectionné: ${file.name}`);

    setScanning(true);
    try {
      console.log('Uploading file...');
      const response = await receiptsApi.scan(file);
      console.log('Upload response:', response.data);
      
      if (response.data.success && response.data.extracted) {
        const extracted = response.data.extracted;
        setScannedData(response.data);
        setFormData(prev => ({
          ...prev,
          amount: extracted.amount?.toString() || '',
          description: extracted.description || '',
          merchant: extracted.merchant || '',
          category: extracted.category || 'autre',
          expense_date: extracted.date || prev.expense_date,
          receipt_id: response.data.file_id,
        }));
        toast.success('Facture analysée avec succès');
      } else {
        setFormData(prev => ({ ...prev, receipt_id: response.data.file_id }));
        toast.info('Facture téléchargée. Veuillez remplir les informations manuellement.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur inconnue';
      toast.error(`Erreur: ${errorMsg}`);
    } finally {
      setScanning(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024, // 20MB max
    disabled: scanning,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id) {
      toast.error('Veuillez sélectionner une propriété');
      return;
    }
    try {
      await expensesApi.create({
        ...formData,
        amount: parseFloat(formData.amount) || 0,
      });
      toast.success('Dépense ajoutée');
      setDialogOpen(false);
      resetForm();
      loadExpenses();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await expensesApi.delete(id);
      toast.success('Dépense supprimée');
      loadExpenses();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: selectedProperty !== 'all' ? selectedProperty : '',
      category: 'autre',
      description: '',
      amount: '',
      expense_date: new Date().toISOString().split('T')[0],
      merchant: '',
      receipt_id: '',
    });
    setScannedData(null);
  };

  // Calculate totals
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const categoryTotals = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + (e.amount || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">Dépenses</h1>
          <p className="text-muted-foreground mt-1 text-sm">Gérez vos factures et dépenses</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow" data-testid="add-expense-button">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Nouvelle dépense</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Receipt Scanner */}
              <div className="space-y-2">
                <Label>Scanner une facture (optionnel)</Label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors relative z-[60] ${
                    isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  } ${scanning ? 'pointer-events-none opacity-50' : ''}`}
                  data-testid="receipt-dropzone"
                >
                  <input {...getInputProps()} />
                  {scanning ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Analyse en cours...</p>
                    </div>
                  ) : scannedData ? (
                    <div className="flex flex-col items-center gap-2 text-primary">
                      <Camera className="h-8 w-8" />
                      <p className="text-sm">{scannedData.filename}</p>
                      <p className="text-xs text-muted-foreground">Facture analysée - vérifiez les informations</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Camera className="h-8 w-8" />
                      <p className="text-sm">Glissez une photo ou cliquez pour scanner</p>
                      <p className="text-xs">JPG, PNG ou PDF</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Propriété *</Label>
                  <Select value={formData.property_id} onValueChange={(v) => setFormData({ ...formData, property_id: v })}>
                    <SelectTrigger data-testid="expense-property-select" className="w-full">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[200px] overflow-y-auto z-[100]">
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger data-testid="expense-category-select" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[100]">
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de la dépense"
                  required
                  data-testid="expense-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="expense-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.expense_date}
                    onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                    required
                    data-testid="expense-date"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fournisseur</Label>
                <Input
                  value={formData.merchant}
                  onChange={(e) => setFormData({ ...formData, merchant: e.target.value })}
                  placeholder="Nom du fournisseur"
                  data-testid="expense-merchant"
                />
              </div>

              <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="expense-submit">
                Ajouter la dépense
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border/40">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
            <p className="text-xl md:text-2xl font-semibold tabular-nums text-red-400">-€{totalExpenses.toLocaleString()}</p>
          </CardContent>
        </Card>
        {Object.entries(categoryTotals).slice(0, 3).map(([cat, total]) => (
          <Card key={cat} className="bg-card border-border/40">
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider capitalize">{cat}</p>
              <p className="text-xl md:text-2xl font-semibold tabular-nums">€{total.toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Expenses List */}
      <Card className="bg-card border-border/40">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/40 hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Catégorie</TableHead>
                  <TableHead className="text-muted-foreground">Description</TableHead>
                  <TableHead className="text-muted-foreground hidden md:table-cell">Fournisseur</TableHead>
                  <TableHead className="text-muted-foreground text-right">Montant</TableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">Chargement...</TableCell>
                  </TableRow>
                ) : expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Aucune dépense. Ajoutez votre première dépense !
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((exp) => (
                    <TableRow key={exp.id} className="border-border/40" data-testid={`expense-row-${exp.id}`}>
                      <TableCell className="tabular-nums text-sm">
                        {format(parseISO(exp.expense_date), 'd MMM yyyy', { locale: fr })}
                      </TableCell>
                      <TableCell>
                        <Badge className={CATEGORY_COLORS[exp.category] || CATEGORY_COLORS.autre}>
                          {exp.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{exp.description}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">{exp.merchant || '-'}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-red-400">
                        -€{exp.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(exp.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                          data-testid={`delete-expense-${exp.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
